import { type FC, useMemo } from "react";

import ChooseEntityVariantView, {
  ChooseEntityVariantProvider,
} from "@/components/ChooseEntityDialog/ChooseEntityDialogContent/ChooseEntityVariantView";
import type { EntitySelection } from "@/components/ChooseEntityDialog/ChooseEntityDialogContent/ChooseEntityVariantView/choose-entity-variant-context";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useUser } from "@/context/user-context";
import type { Entity } from "@/queries";
import { EntityType } from "@/queries/folder-entities/types";
import { useGetToolRegistry, useListToolVersions } from "@/queries/tool-registry";
import type { RegistryTool } from "@/types";

interface EditRegistryToolDialogProps {
  isOpen: boolean;
  onClose: () => void;
  registryTool: RegistryTool;
  displayName: string;
  onSave: (next: RegistryTool) => void;
}

const EditRegistryToolDialog: FC<EditRegistryToolDialogProps> = ({
  isOpen,
  onClose,
  registryTool,
  displayName,
  onSave,
}) => {
  const userContext = useUser();
  const workspaceId = userContext?.activeWorkspaceId ?? null;

  // Versions feed the picker's version list AND let us derive _has_execution for the pick.
  const versionsQuery = useListToolVersions(workspaceId, registryTool.tool_registry_id);
  const allVersions = useMemo(
    () => versionsQuery.data?.pages.flatMap(p => p.versions) ?? [],
    [versionsQuery.data],
  );
  const latestVersionNumber = useMemo(
    () => allVersions.reduce((max, v) => (v.number > max ? v.number : max), 0) || undefined,
    [allVersions],
  );

  // Labels resolve to a tool_version_id; cross-ref with allVersions for execution.
  const registryQuery = useGetToolRegistry(workspaceId, registryTool.tool_registry_id);
  const labels = registryQuery.data?.labels ?? [];

  const entity = useMemo<Entity<EntityType.TOOL>>(
    () => ({
      id: registryTool.tool_registry_id,
      name: displayName,
      type: EntityType.TOOL,
      path: [],
      label: registryTool.label ?? undefined,
      version_number: registryTool.version_number ?? undefined,
      metadata: { latest_version_number: latestVersionNumber },
    }),
    [
      registryTool.tool_registry_id,
      registryTool.label,
      registryTool.version_number,
      displayName,
      latestVersionNumber,
    ],
  );

  const resolveHasExecution = (selection: EntitySelection): boolean => {
    if (selection.type === "version") {
      const targetNumber = selection.version ?? latestVersionNumber;
      const target = allVersions.find(v => v.number === targetNumber);
      return target?.execution !== null && target?.execution !== undefined;
    }
    const label = labels.find(l => l.name === selection.label);
    if (!label) {
      return false;
    }
    const target = allVersions.find(v => v.id === label.tool_version_id);
    return target?.execution !== null && target?.execution !== undefined;
  };

  const handleSelect = (selection: EntitySelection) => {
    const next: RegistryTool = {
      ...registryTool,
      label: selection.type === "label" ? selection.label : null,
      version_number: selection.type === "version" ? (selection.version ?? null) : null,
      _has_execution: resolveHasExecution(selection),
    };
    onSave(next);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className="flex h-full max-h-[720px] max-w-4xl flex-col gap-0 p-0">
        <ChooseEntityVariantProvider
          entity={entity}
          selectedEntities={[entity]}
          onSelect={handleSelect}
          onOpenChange={open => !open && onClose()}
          onBack={onClose}
        >
          <ChooseEntityVariantView />
        </ChooseEntityVariantProvider>
      </DialogContent>
    </Dialog>
  );
};

export default EditRegistryToolDialog;
